import type { SearchPollPoint } from './searchFilters';

/**
 * Client-side stopwatch for one search run: the click on Search to the
 * frame that painted the first results.
 *
 * First paint, not completion, because that is the wait being studied — the
 * dead time between asking and seeing anything. What happens after the first
 * rows land is the run filling in, which the user is already reading through.
 *
 * Why this exists alongside the server's timing waterfall: `total_ms` is what
 * the run cost the backend, but the reader only discovers a run has anything
 * to show by polling every RUN_POLL_INTERVAL_MS. What a user waits for is
 * therefore the server time, plus a round trip, plus up to one poll interval
 * of pure ignorance, plus a render. Only the browser can measure that, and
 * only measuring it makes the polling overhead visible instead of assumed.
 *
 * So the mark carries three numbers and never collapses them:
 *  - `observedMs` — click to painted pixels. What the user actually waited,
 *    and the only number here that is a measurement of the experience.
 *  - `pollGapMs` — the width of the poll interval that delivered the
 *    milestone. The run became ready somewhere inside it, so what can be
 *    claimed about the server is the range `[observed - gap, observed]`,
 *    never a point. Reporting a point is the specific error this field exists
 *    to prevent.
 *  - `serverMs` — the run's own `total_ms`, when it reported one.
 *
 * `observedMs - serverMs` is the part that polling, the network and rendering
 * add on top of the search itself: the number to watch when deciding whether
 * the poll loop is worth replacing.
 */

export interface SearchStopwatchMark {
  /** Click to painted pixels, in ms. */
  observedMs: number;
  /** Width of the poll interval that delivered this milestone, in ms. */
  pollGapMs: number;
  /** The run's server-side total, or null before the run reported one. */
  serverMs: number | null;
}

export interface SearchStopwatchRun {
  /** First results on screen. Null until the run has painted something. */
  firstPaint: SearchStopwatchMark | null;
  /**
   * False once the tab was hidden at any point during the run. rAF is
   * throttled in a background tab, so those timings are fiction and must not
   * be shown or folded into a median.
   */
  trusted: boolean;
}

/**
 * How long the poll loop went without news before the poll that delivered the
 * current milestone — i.e. how much of `observedMs` is time the run may have
 * already been ready and nobody had asked yet.
 *
 * With a single poll the whole elapsed time is that window: the run could
 * have been ready immediately after it started.
 */
export function pollGapMs(points: SearchPollPoint[]): number {
  if (points.length === 0) return 0;
  const latest = points[points.length - 1];
  if (points.length === 1) return Math.max(0, latest.elapsedMs);
  return Math.max(0, latest.elapsedMs - points[points.length - 2].elapsedMs);
}

/**
 * The wait the search itself does not explain: network, poll wait and render.
 * Null when the run reported no server total; clamped at zero, since the two
 * clocks are different and a slightly negative difference means "nothing
 * measurable", not "faster than instant".
 */
export function overheadMs(mark: SearchStopwatchMark): number | null {
  if (mark.serverMs === null) return null;
  return Math.max(0, mark.observedMs - mark.serverMs);
}

/** For the breakdown, where sub-second parts read better in milliseconds. */
export function formatStopwatchMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(ms >= 10_000 ? 1 : 2)}s`;
}

/**
 * The face of the stopwatch. Always seconds to two places, because this
 * number is redrawn every frame while a run is in flight and a format that
 * switches units or precision as it climbs would jump the digits around
 * rather than count.
 */
export function formatStopwatchSeconds(ms: number): string {
  return `${Math.max(0, ms / 1000).toFixed(2)}s`;
}

/** Median, for reading a handful of runs without letting one outlier lead. */
export function medianMs(values: number[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[middle];
  return (sorted[middle - 1] + sorted[middle]) / 2;
}

/**
 * The face's frozen reading once results have landed, or null while the run
 * is still counting and the display should show live elapsed time instead.
 */
export function stopwatchReading(run: SearchStopwatchRun | null): number | null {
  if (!run || !run.trusted) return null;
  return run.firstPaint ? run.firstPaint.observedMs : null;
}

function markLine(label: string, mark: SearchStopwatchMark): string {
  const parts = [`${label}: ${formatStopwatchMs(mark.observedMs)} to pixels`];
  if (mark.pollGapMs > 0) {
    parts.push(`up to ${formatStopwatchMs(mark.pollGapMs)} of it poll wait`);
  }
  const overhead = overheadMs(mark);
  if (overhead !== null && mark.serverMs !== null) {
    parts.push(`server run ${formatStopwatchMs(mark.serverMs)}, +${formatStopwatchMs(overhead)} network/poll/render`);
  }
  return parts.join(' · ');
}

/**
 * The full readout, for the face's tooltip. Multi-line on purpose: these
 * numbers mean different things and a single line invites reading them as one
 * measurement with error bars.
 */
export function stopwatchTitle(
  run: SearchStopwatchRun | null,
  history: SearchStopwatchRun[] = [],
): string {
  if (!run) return 'Times every search from click to first painted results (admin mode)';
  if (!run.trusted) {
    return 'Tab was hidden during this run — rAF is throttled in the background, so the timing was discarded.';
  }
  const lines: string[] = [];
  if (run.firstPaint) lines.push(markLine('First results', run.firstPaint));
  if (lines.length === 0) lines.push('Timing this run...');
  const summary = historySummary(history);
  if (summary) lines.push(summary);
  return lines.join('\n');
}

/**
 * Median across the trusted, painted runs of this session. One search is
 * noise; this is what makes a handful of clicks say something.
 */
export function historySummary(history: SearchStopwatchRun[]): string | null {
  const waits = history
    .filter((run) => run.trusted && run.firstPaint)
    .map((run) => (run.firstPaint as SearchStopwatchMark).observedMs);
  if (waits.length < 2) return null;
  const median = medianMs(waits) as number;
  return `median of ${waits.length} runs: ${formatStopwatchMs(median)} to first results`;
}

/**
 * Whether a run that began somewhere other than the Search button should be
 * picked up and timed.
 *
 * Opening a saved search runs it from the page, not from the composer, so no
 * click ever reaches the stopwatch and the face would sit at 0.00s through
 * the whole wait and then stop there — the one reading that is never true.
 * The run becoming visible (`populating` turning true) is the earliest the
 * composer can know it exists, so that edge starts the clock.
 *
 * `runActive` is what keeps a button-started run from being started twice:
 * its own click already set the zero, and `populating` follows a render
 * later.
 */
export function shouldAdoptRun(
  { populating, wasPopulating, runActive }:
  { populating: boolean; wasPopulating: boolean; runActive: boolean },
): boolean {
  return populating && !wasPopulating && !runActive;
}

/**
 * The instant the user asked for results.
 *
 * A click event's `timeStamp` shares `performance.now()`'s time origin in
 * every browser the reader supports, and starting there rather than at the
 * top of the handler keeps the input-latency gap — the part of the wait that
 * happens before any of our code runs — inside the measurement. Values that
 * cannot be on that timebase (jsdom's 0, a synthetic event, a clock that
 * claims the future) fall back to now.
 */
export function stopwatchStart(clickTimeMs: number | undefined, nowMs: number): number {
  if (typeof clickTimeMs !== 'number' || !Number.isFinite(clickTimeMs)) return nowMs;
  if (clickTimeMs <= 0 || clickTimeMs > nowMs) return nowMs;
  return clickTimeMs;
}
